<template>
  <div class="mt-2">
    <v-alert v-if="error" type="error" class="mb-0">
      <v-row align="center">
        <v-col class="grow py-0">
          {{ error }}
        </v-col>
        <v-col class="shrink py-0">
          <v-btn small @click="$emit('refresh')" :disabled="blockRefresh">
            Tentar novamente
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>
    <template v-else-if="(loading && !payments.length) || loadingEssentials">
      <v-progress-linear indeterminate color="info" class="mt-2" />
      <v-alert type="info" text class="mt-0 mb-0">
        Carregando relatório, por favor aguarde...
        <p v-if="oldEvent" class="mb-0">
          Eventos antigos podem demorar mais para carregar
        </p>
      </v-alert>
    </template>
    <v-alert
      v-else-if="!loading && !payments.length"
      class="mb-0"
      type="info"
      text
    >
      Nenhum pagamento encontrado
    </v-alert>
    <div v-else class="d-flex flex-column gap-2">
      <div class="d-flex align-center mx-1">
        <v-btn
          @click="exportExcel"
          text
          x-small
          depressed
          color="primary"
          :loading="exporting === 'excel'"
          :disabled="!!exporting || loading || isOldData"
        >
          <v-icon x-small left>mdi-file-excel</v-icon>
          Exportar Excel
        </v-btn>
        <v-btn
          @click="exportPDF"
          text
          x-small
          depressed
          color="primary"
          :loading="exporting === 'pdf'"
          :disabled="!!exporting || loading || isOldData"
        >
          <v-icon x-small left>mdi-file-pdf-box</v-icon>
          Exportar PDF
        </v-btn>
        <v-spacer />
        <v-btn
          @click="$emit('refresh')"
          icon
          small
          :loading="loading"
          :disabled="blockRefresh"
        >
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
        <small>
          Última atualização:
          {{ lastUpdate | date("DD/MM [às] HH:mm:ss") }}
        </small>
      </div>
      <v-alert
        v-if="loading && isOldData"
        class="mb-2"
        icon="mdi-update"
        type="warning"
        text
      >
        Os dados estão {{ [lastUpdate] | dateDiff }} desatualizados. Por favor
        aguarde enquanto buscamos os dados mais recentes.
      </v-alert>
      <ReportFilters v-model="filters" :ticketGroups="ticketGroups" />
      <ResumeHighlights
        :party="party"
        :payments="filteredData.payments"
        :tickets="filteredData.tickets"
        :loading="loading"
      />
      <v-alert
        v-if="hiddenTicketBlocks.size"
        :type="showHiddenTicketblocks ? 'success' : 'warning'"
        class="ma-0"
        dense
        text
      >
        <div class="d-flex flex-wrap align-center justify-space-between gap-2">
          <template v-if="showHiddenTicketblocks">
            Todos os lotes estão habilitados no relatório.
          </template>
          <template v-else>
            {{ hiddenTicketBlocks.size }} lotes estão desabilitados no
            relatório.
          </template>
          <v-btn v-if="!showHiddenTicketblocks" small @click="showHiddenLots">
            Mostrar lotes desabilitados
          </v-btn>
          <v-btn v-else small @click="showHiddenTicketblocks = false">
            Ocultar lotes desabilitados
          </v-btn>
        </div>
      </v-alert>

      <div class="d-flex flex-column gap-6 mt-2">
        <ResumeSalesByTicketGroup
          :party="party"
          :payments="filteredData.payments"
          :tickets="filteredData.tickets"
          :loading="loading"
          :sellers="sellers"
          :posSellers="posSellers"
          :saleLinks="saleLinks"
          :ticketGroups="ticketGroups"
          :ticketBlockMap="ticketBlockMap"
        />
        <ResumePaymentMethods
          :party="party"
          :payments="filteredData.payments"
          :tickets="filteredData.tickets"
          :loading="loading"
          :sellers="sellers"
        />
      </div>
    </div>
    <party-report-show-hidden-ticketblocks
      ref="showHiddenTicketblocksModal"
      :hiddenTicketBlocks="hiddenTicketBlocks"
      :ticketBlocks="ticketBlockMap"
    />
  </div>
</template>

<script>
import moment from "moment";
import paymentService from "@/services/admin/report";
import ORGANIZATION from "@/services/admin/organization";
import TICKET from "@/services/admin/ticket";
import SELLER from "@/services/admin/party/seller";

import filterFunction from "./reports/filters/filterFunction";
import ResumeSalesByTicketGroup from "./reports/ResumeSalesByTicketGroup.vue";
import ResumePaymentMethods from "./reports/ResumePaymentMethods.vue";
import ResumeHighlights from "./reports/ResumeHighlights.vue";
import PartyReportShowHiddenTicketblocks from "./PartyReportShowHiddenTicketblocks.vue";
import ReportFilters from "./reports/filters/ReportFilters.vue";

import exportExcel from "@/utils/exportExcel/salesResume";
import exportPDF from "@/components/admin/party/reports/export/reports/resume/generator";

export default {
  name: "PartyResumeReport",
  components: {
    ReportFilters,
    ResumeHighlights,
    ResumePaymentMethods,
    ResumeSalesByTicketGroup,
    PartyReportShowHiddenTicketblocks,
  },
  props: {
    party: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadingEssentials: false,
      exporting: null,
      filters: null,
      showHiddenTicketblocks: false,
    };
  },
  computed: {
    isOldData() {
      return (
        this.lastUpdate &&
        moment(this.lastUpdate).isBefore(moment().subtract(5, "minutes"))
      );
    },
    oldEvent() {
      return moment(this.party.date).isBefore(moment().subtract(3, "months"));
    },
    filteredData() {
      const { payments, tickets } = this.payments.reduce(
        filterFunction({
          filters: this.filters,
          showHiddenTicketblocks: this.showHiddenTicketblocks,
          hiddenTicketBlocks: this.hiddenTicketBlocks,
          status: "succeeded",
        }),
        { payments: [], tickets: [] }
      );

      return { payments, tickets };
    },
    hiddenTicketBlocks() {
      return this.ticketGroups.reduce((acc, tg) => {
        tg.TicketBlock.forEach((tb) => {
          if (tb.hideInReport) acc.set(tb.id, tb);
        });
        return acc;
      }, new Map());
    },
  },
  methods: {
    async showHiddenLots() {
      this.showHiddenTicketblocks =
        await this.$refs.showHiddenTicketblocksModal.open();
    },
    async exportExcel() {
      try {
        this.exporting = "excel";

        await exportExcel({
          party: this.party,
          tickets: this.filteredData.tickets,
          payments: this.filteredData.payments,
          filters: this.filters,
          ticketBlocksMap: this.ticketBlockMap,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.exporting = false;
      }
    },
    async exportPDF() {
      try {
        this.exporting = "pdf";

        const document = await exportPDF({
          party: this.party,
          reportData: this.filteredData.payments,
          filter: this.filters,
          ticketBlocksMap: this.ticketBlockMap,
        });

        const name = `Resumo de vendas - ${this.party.name}`;
        const generatedAt = new Date().toLocaleString("pt-BR");

        document.addFooter((doc, { currentPage, totalPages }) => {
          const pageWidth = doc.internal.pageSize.width;
          const pageHeight = doc.internal.pageSize.height;
          doc.setFontSize(8);
          doc.text(`${name} • Gerado em: ${generatedAt}`, 10, pageHeight - 5);

          // Adiciona o texto à direita, alinhado ao lado direito da página
          const currentPageText = `Página ${currentPage} de ${totalPages}`;
          doc.text(
            currentPageText,
            pageWidth - doc.getTextWidth(currentPageText) - 10,
            pageHeight - 5
          );
        });

        document.save(name);
      } catch (error) {
        console.error(error);
      } finally {
        this.exporting = false;
      }
    },
  },
  props: {
    party: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String | Boolean | null,
      default: false,
    },
    lastUpdate: {
      type: Date,
      default: null,
    },
    blockRefresh: {
      type: Boolean,
      default: false,
    },
    refreshKey: {
      type: Number,
      default: 0,
    },
    tickets: {
      type: Array,
      default: () => [],
    },
    payments: {
      type: Array,
      default: () => [],
    },
    ticketGroups: {
      type: Array,
      default: () => [],
    },
    posSellers: {
      type: Array,
      default: () => [],
    },
    saleLinks: {
      type: Array,
      default: () => [],
    },
    sellers: {
      type: Array,
      default: () => [],
    },
    ticketBlockMap: {
      type: Map,
      default: () => new Map(),
    },
  },
};
</script>
